import React, {
  useState, useMemo, useCallback, useEffect, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch, batch } from 'react-redux';
import { useTable, useSortBy } from 'react-table';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Avatar from '../../../../shared/_TBComponents/Avatar/Avatar';
import Button from '../../../../shared/_TBComponents/Button';
import Icon from '../../../../shared/_TBComponents/Icon';
import Input from '../../../../shared/_TBComponents/Input';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import Tooltip from '../../../../shared/_TBComponents/Tooltip';
import {
  fetchUser, fetchAthleteAssignments, fetchArchivedAthletes,
} from '../../ducks/navigatorActions';

const SearchRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const TabsRow = styled.div`
  display: flex;
  margin: 16px 0;
  flex-shrink: 0;
`;

const TabButton = styled.button`
  padding: 0 4px 8px 4px;
  margin-right: 24px;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: ${(props) => (props.active ? 700 : 400)};
  color: ${(props) => (props.active ? '#444' : '#616161')};
  border: none;
  border-bottom: 3px solid ${(props) => (props.active ? '#1787FB' : 'transparent')};
  background: none;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: #333333;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 560px;
  display: flex;
  align-items: center;

  input {
    border: 1px solid var(--theme-border-default, #EEE);
    border-radius: 4px;
    font-family: "Nunito Sans";

    &::placeholder {
      color: #9E9E9E;
      font-weight: 400;
    }
  }

  @media (max-width: 767px) {
    max-width: 100%;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 45%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 2;
  display: flex;
  align-items: center;
`;

const ResultsText = styled.div`
  font-family: "Nunito Sans";
  font-size: 13px;
  color: #9E9E9E;
  margin-bottom: 10px;
`;

const TableContainer = styled.div`
  font-family: "Nunito Sans";
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #EEEEEE;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
`;

const HScroller = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  background: #FAFAFA;
  border-bottom: 1px solid #EEEEEE;
  position: sticky;
  top: 0;
  z-index: 1;
  flex-shrink: 0;
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 12px 16px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  color: #9E9E9E;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: ${(props) => (props.canSort ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    background: ${(props) => (props.canSort ? '#F0F0F0' : 'transparent')};
  }
`;

const VirtualBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const VirtualRow = styled.div`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #F5F5F5;
  cursor: pointer;
  transition: background 0.1s ease;
  box-sizing: border-box;
  ${(props) => props.isSelected && 'background: #F7F9FF;'}
  &:hover { background: #F7F9FF; }
`;

const VirtualCell = styled.div`
  display: flex;
  align-items: center;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #333333;
  flex-shrink: 0;
  box-sizing: border-box;
  overflow: hidden;
`;

const GroupsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const GroupBadge = styled.span`
  display: inline-block;
  background: #EEEEEE;
  border-radius: 2px;
  padding: 0 4px;
  color: var(--theme-fg-subtle, #9E9E9E);
  text-align: center;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  white-space: nowrap;
`;

const RowCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #444444;
`;

const EmptyStateDiv = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9E9E9E;
  font-size: 14px;
`;

const StatusLabel = styled.div`
  display: inline-block;
  font-size: 14px;
  font-weight: 700;
  border-radius: 2px;
  padding: 0 12px;
  color: ${(props) => (props.registered ? '#0DCC8A' : '#FFBE19')};
  background: ${(props) => (props.registered ? '#0DCC8A1A' : '#FFCB471A')};
  cursor: ${(props) => (props.registered ? 'default' : 'pointer')};
`;

const ActionDropdownWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const ActionMenuButton = styled.button`
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  rotate: 90deg;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

const ActionMenu = styled.div`
  background: #FFF;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 6px;
  z-index: 9999;
  min-width: 190px;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #EEEEEE;
  margin: 2px 8px;
`;

const ActionMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border-radius: 6px;
  font-family: "Nunito Sans";
  font-size: 16px;
  color: #333333;
  background: none;
  border: none;
  cursor: pointer;

  svg {
    display: block;
    flex-shrink: 0;
    margin-bottom: 2px;
  }

  &:hover {
    background: #F7F7F7;
  }
`;

const SpinnerContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BulkActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 16px;
`;

const BulkDropdownContainer = styled.div`
  position: relative;
`;

const BulkDropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  margin-left: 8px;
  background: #444444;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #333333;
  }
`;

const BulkDropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  background: #fff;
  border: 1px solid #EEEEEE;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  min-width: 225px;
  z-index: 10;
  overflow: hidden;
`;

const BulkDropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 10px 16px;
  font-family: "Nunito Sans";
  font-size: 16px;
  line-height: 1;
  color: #333333;
  background: none;
  border: none;
  cursor: pointer;

  svg {
    display: block;
    flex-shrink: 0;
    margin-bottom: 2px;
  }

  &:hover {
    background: #F7F7F7;
  }
`;

const BulkDropdownDivider = styled.div`
  height: 1px;
  background: #EEEEEE;
  margin: 0 12px;
`;

const FilterButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const FilterBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  display: flex;
  width: 16px;
  height: 16px;
  justify-content: center;
  align-items: center;
  background: #000;
  border-radius: 50%;
  color: #FFF;
  font-family: "Nunito Sans";
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
`;

const ArchiveBanner = styled.div`
  background-color: #F8F9FA;
  border: 1px solid #DEE2E6;
  border-radius: 6px;
  padding: 10px 16px;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 4px;
`;

// Cell Components //

const SelectHeaderCell = ({ column }) => (
  <RowCheckbox
    ref={column.selectAllRef}
    type='checkbox'
    checked={column.allSelected}
    onChange={column.toggleAll}
  />
);

SelectHeaderCell.propTypes = {
  column: PropTypes.shape({
    selectAllRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    allSelected: PropTypes.bool,
    toggleAll: PropTypes.func,
  }).isRequired,
};

const SelectCell = ({ row }) => (
  <RowCheckbox
    type='checkbox'
    checked={row.original.isSelected}
    onChange={() => row.original.onToggle(row.original.id)}
    onClick={(e) => e.stopPropagation()}
  />
);

SelectCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      isSelected: PropTypes.bool,
      onToggle: PropTypes.func,
    }),
  }).isRequired,
};

const AvatarCell = ({ row }) => {
  const { pic, first, last } = row.original;
  return (
    <Avatar
      avatar={pic}
      name={`${first} ${last}`}
      size={36}
    />
  );
};

AvatarCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      pic: PropTypes.string,
      first: PropTypes.string,
      last: PropTypes.string,
    }),
  }).isRequired,
};

const GroupsCell = ({ row }) => {
  const { groupAssignments, id } = row.original;
  if (!groupAssignments || groupAssignments.length === 0) return <Text styledTextProps={{ color: '#CCCCCC' }}>–</Text>;

  const shown = groupAssignments.slice(0, 2);
  const extra = groupAssignments.length - 2;
  const hoverText = groupAssignments.map((g) => g.name).join(', ');

  return (
    <GroupsWrapper>
      <span>{shown.map((g) => g.name).join(', ')}</span>
      {extra > 0 && (
        <GroupBadge>
          <Tooltip
            id={`groups-tooltip-${id}`}
            displayText={`+ ${extra} more`}
            hoverText={hoverText}
            displaySize='14px'
            displayColor='var(--theme-fg-subtle, #9E9E9E)'
            displayWeight='400'
            cursor='default'
            place='top'
          />
        </GroupBadge>
      )}
    </GroupsWrapper>
  );
};

GroupsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      groupAssignments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })),
    }),
  }).isRequired,
};

const CalendarCell = ({ value }) => (value ? <span>{value}</span> : <Text styledTextProps={{ color: '#CCCCCC' }}>–</Text>);

CalendarCell.propTypes = {
  value: PropTypes.string,
};

CalendarCell.defaultProps = {
  value: '',
};

const StatusCell = ({ row }) => {
  const registered = row.original.activated === 1;
  return (
    <StatusLabel
      registered={registered}
      onClick={registered ? undefined : (e) => {
        e.stopPropagation();
        row.original.onResendActivation(row.original);
      }}
    >
      {registered ? 'Registered' : 'Pending'}
    </StatusLabel>
  );
};

StatusCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      activated: PropTypes.number,
      onResendActivation: PropTypes.func,
    }),
  }).isRequired,
};

// Manages action menu open state, positioning, and outside click handling
const useActionMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const openMenu = (e) => {
    e.stopPropagation();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < 190;
      setMenuStyle({
        position: 'fixed',
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target)
        && buttonRef.current && !buttonRef.current.contains(e.target)
      ) setIsOpen(false);
    };
    const closeOnScroll = () => setIsOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', closeOnScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [isOpen]);

  return {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close: () => setIsOpen(false),
  };
};

const ActionDropdownCell = ({ row }) => {
  const {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close,
  } = useActionMenu();

  return (
    <ActionDropdownWrapper onClick={(e) => e.stopPropagation()}>
      <ActionMenuButton ref={buttonRef} type='button' onClick={openMenu}>
        <Icon icon='dots' size={16} color='#9E9E9E' />
      </ActionMenuButton>
      {isOpen && createPortal(
        <ActionMenu ref={menuRef} style={menuStyle}>
          <ActionMenuItem type='button' onClick={() => { row.original.onApa(row.original); close(); }}>
            <Icon icon='pr-tracking' size={16} color='#9E9E9E' />
            View Assessment
          </ActionMenuItem>
          <ActionMenuItem type='button' onClick={() => { row.original.onEdit(row.original); close(); }}>
            <Icon icon='edit-workout' size={16} color='#9E9E9E' />
            Edit
          </ActionMenuItem>
          <ActionMenuItem type='button' onClick={() => { row.original.onArchive(row.original); close(); }}>
            <Icon icon='archive' size={16} color='#9E9E9E' />
            Archive
          </ActionMenuItem>
          <MenuDivider />
          <ActionMenuItem destructive type='button' onClick={() => { row.original.onDelete(row.original); close(); }}>
            <Icon icon='delete' size={16} color='#FF0505' />
            Delete
          </ActionMenuItem>
        </ActionMenu>,
        document.body,
      )}
    </ActionDropdownWrapper>
  );
};

ActionDropdownCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onApa: PropTypes.func,
      onEdit: PropTypes.func,
      onArchive: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

const ArchivedActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ArchivedActionsCell = ({ row }) => (
  <ArchivedActionsWrapper onClick={(e) => e.stopPropagation()}>
    <Button
      name='Restore'
      icon='restore'
      iconColor='#FFF'
      iconSize={13}
      bgColor='#444444'
      borderColor='#444444'
      txtColor='#FFF'
      fontSize='14px'
      customBorderRadius='4px'
      styledWrapperProps={{ padding: '8px 16px' }}
      styledTextProps={{ fontWeight: 700 }}
      onClick={() => row.original.onRestore(row.original)}
    />
    <Button
      name='Delete'
      icon='delete'
      iconColor='#FF0505'
      iconSize={13}
      bgColor='#FFF'
      txtColor='#444444'
      fontSize='14px'
      customBorderRadius='4px'
      styledWrapperProps={{ padding: '8px 16px', border: '2px solid #EEEEEE' }}
      styledTextProps={{ fontWeight: 700 }}
      onClick={() => row.original.onDelete(row.original)}
    />
  </ArchivedActionsWrapper>
);

ArchivedActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onRestore: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

const OverflowCellSpan = styled.span`
  display: block;
  overflow: hidden;
  width: 100%;
  word-break: break-word;
`;

const OverflowCell = ({ value }) => (
  <OverflowCellSpan>
    {value || <span style={{ color: '#CCCCCC' }}>–</span>}
  </OverflowCellSpan>
);

OverflowCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

OverflowCell.defaultProps = {
  value: '',
};

const nameSort = (rowA, rowB, columnId) => {
  const a = (rowA.values[columnId] || '').toLowerCase();
  const b = (rowB.values[columnId] || '').toLowerCase();
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

const SortIcon = ({ column }) => (
  <Icon
    icon={column.isSorted ? (column.isSortedDesc ? 'alt-down-arrow' : 'alt-up-arrow') : 'Arrows-Sort'}
    size={12}
    color={column.isSorted ? '#77A7FF' : '#DFDFDF'}
    styledProps={{ marginBottom: '2px' }}
  />
);

SortIcon.propTypes = {
  column: PropTypes.shape({
    isSorted: PropTypes.bool,
    isSortedDesc: PropTypes.bool,
  }).isRequired,
};

const ROW_HEIGHT = 80;

const RowRenderer = ({ index, style, data }) => {
  const { rows, prepareRow } = data;
  const row = rows[index];
  prepareRow(row);
  return (
    <VirtualRow
      style={style}
      isSelected={row.original.isSelected}
      onClick={() => row.original.onToggle && row.original.onToggle(row.original.id)}
    >
      {row.cells.map((cell) => {
        const cs = cell.column.cellStyle || {};
        const hs = cell.column.headerStyle || {};
        return (
          <VirtualCell
            key={cell.column.id}
            style={{
              flex: cs.flex || hs.flex,
              width: (cs.flex || hs.flex) ? undefined : (cs.width || hs.width),
              minWidth: cs.minWidth || hs.minWidth,
              padding: cs.padding || '10px 16px',
              textAlign: cs.textAlign,
              overflow: cs.overflow || 'hidden',
              textOverflow: cs.textOverflow,
              whiteSpace: cs.whiteSpace,
              justifyContent: cs.textAlign === 'center' ? 'center' : undefined,
            }}
          >
            {cell.render('Cell')}
          </VirtualCell>
        );
      })}
    </VirtualRow>
  );
};

RowRenderer.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.number,
        isSelected: PropTypes.bool,
        onToggle: PropTypes.func,
      }),
      cells: PropTypes.arrayOf(PropTypes.shape({})),
    })).isRequired,
    prepareRow: PropTypes.func.isRequired,
  }).isRequired,
};

const ManageAthletesTable = ({
  onEditAthlete, onDeleteAthlete, onResendActivation, onArchiveAthlete,
  onRestoreAthlete, isArchived, onToggleArchived, archivedUserSlots, onOpenSupport, onBulkAction,
  columnVisibility, onOpenFilter, hasActiveFilters, activeFiltersCount, onVisibleAthletesChange,
}) => {
  const dispatch = useDispatch();

  const athletes = useSelector((state) => state.navigator.data.athletes);
  const archivedAthletes = useSelector((state) => state.navigator.data.archivedAthletes);
  const calendars = useSelector((state) => state.navigator.data.calendars);
  const isLoadingAthletes = useSelector((state) => state.navigator.ui.isLoadingAthletes);
  const isLoadingArchivedAthletes = useSelector(
    (state) => state.navigator.ui.isLoadingArchivedAthletes,
  );
  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const accountsList = useSelector((state) => state.auth.data.currentUser?.accountsList);
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );
  const orgSettings = useSelector((state) => state.auth.data.organizationSettings);
  const smartabaseEnabled = orgSettings?.smartabaseEnabled;
  const ssoEnabled = orgSettings?.sso?.enabled;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isBulkDropdownOpen, setIsBulkDropdownOpen] = useState(false);
  const selectAllRef = useRef(null);
  const bulkDropdownRef = useRef(null);

  // Removes any selected athlete IDs that no longer exist in current list
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const currentList = isArchived ? archivedAthletes : athletes;
    const validIds = new Set(currentList.map((a) => a.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [athletes, archivedAthletes]);

  useEffect(() => {
    if (!isBulkDropdownOpen) return undefined;
    const handleOutsideClick = (e) => {
      if (bulkDropdownRef.current && !bulkDropdownRef.current.contains(e.target)) {
        setIsBulkDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isBulkDropdownOpen]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 100);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (isArchived && accountCode) {
      dispatch(fetchArchivedAthletes(accountCode));
    }
  }, [isArchived, accountCode, dispatch]);

  const calendarMap = useMemo(
    () => calendars.reduce((acc, cal) => {
      acc[cal.id] = cal;
      return acc;
    }, {}),
    [calendars],
  );

  const filteredAthletes = useMemo(() => {
    const source = isArchived ? archivedAthletes : athletes;
    if (!debouncedQuery.trim()) return source;
    const q = debouncedQuery.trim().toLowerCase();
    return source.filter((a) => {
      const fullName = `${a.first || ''} ${a.last || ''}`.toLowerCase();
      const identifierValue = usernamesEnabled ? a.username : a.email;
      if (
        a.first?.toLowerCase().includes(q)
        || a.last?.toLowerCase().includes(q)
        || fullName.includes(q)
        || identifierValue?.toLowerCase().includes(q)
      ) return true;
      if (!isArchived) {
        if (
          String(a.id ?? '').includes(q)
          || String(a.externalId ?? '').toLowerCase().includes(q)
          || String(a.ssoId ?? '').toLowerCase().includes(q)
        ) return true;
      }
      return false;
    });
  }, [isArchived, athletes, archivedAthletes, debouncedQuery, usernamesEnabled]);

  useEffect(() => {
    onVisibleAthletesChange(filteredAthletes);
  }, [filteredAthletes, onVisibleAthletesChange]);

  const allSelected = filteredAthletes.length > 0
    && filteredAthletes.every((a) => selectedIds.has(a.id));
  const someSelected = filteredAthletes.some((a) => selectedIds.has(a.id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = useCallback(() => {
    if (allSelected || someSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAthletes.map((a) => a.id)));
    }
  }, [allSelected, someSelected, filteredAthletes]);

  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Builds table rows with selection state, calendar names, and row actions
  const tableData = useMemo(
    () => filteredAthletes.map((athlete) => ({
      ...athlete,
      calendarName: calendarMap[athlete.calendarAssignment?.calendarId]?.name || '',
      isSelected: selectedIds.has(athlete.id),
      onToggle: toggleOne,
      onResendActivation: (athleteRow) => onResendActivation(athleteRow),
      onApa: (athleteRow) => window.open(`/report-card/user/${athleteRow.id}`, '_blank'),
      onEdit: (athleteRow) => {
        batch(() => {
          dispatch(fetchUser(accountCode, athleteRow.id));
          dispatch(fetchAthleteAssignments(accountCode, athleteRow.id));
        });
        onEditAthlete();
      },
      onArchive: (athleteRow) => onArchiveAthlete(athleteRow),
      onDelete: (athleteRow) => onDeleteAthlete(athleteRow),
    })),
    [filteredAthletes, calendarMap, selectedIds, toggleOne, onEditAthlete, onDeleteAthlete,
      onResendActivation, onArchiveAthlete, accountCode, dispatch],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        id: 'select',
        disableSortBy: true,
        selectAllRef,
        allSelected,
        toggleAll,
        headerStyle: { width: '40px', padding: '4px 8px 4px 16px' },
        cellStyle: { width: '40px', padding: '6px 2px 6px 16px' },
        Header: SelectHeaderCell,
        Cell: SelectCell,
      },
      {
        id: 'avatar',
        disableSortBy: true,
        headerStyle: { width: '52px', padding: '4px 8px 4px 2px' },
        cellStyle: { width: '52px', padding: '6px 8px 6px 2px' },
        Header: '',
        Cell: AvatarCell,
      },
      {
        Header: 'First',
        accessor: 'first',
        sortType: nameSort,
        headerStyle: { width: '190px' },
        Cell: OverflowCell,
      },
      {
        Header: 'Last',
        accessor: 'last',
        sortType: nameSort,
        headerStyle: { width: '190px' },
        Cell: OverflowCell,
      },
      {
        Header: usernamesEnabled ? 'Username' : 'Email',
        accessor: usernamesEnabled ? 'username' : 'email',
        headerStyle: { minWidth: '250px', flex: '1' },
        cellStyle: { minWidth: '250px', flex: '1', overflow: 'hidden' },
        Cell: OverflowCell,
      },
    ];

    if (columnVisibility.userId) {
      cols.push({
        Header: 'User ID',
        accessor: 'id',
        disableSortBy: true,
        headerStyle: { width: '100px', textAlign: 'center' },
        cellStyle: { width: '100px', textAlign: 'center' },
        Cell: OverflowCell,
      });
    }
    if (columnVisibility.externalId) {
      cols.push({
        Header: 'External ID',
        accessor: 'externalId',
        disableSortBy: true,
        headerStyle: { width: '130px', textAlign: 'center' },
        cellStyle: { width: '130px', textAlign: 'center' },
        Cell: OverflowCell,
      });
    }
    if (columnVisibility.ssoId && ssoEnabled) {
      cols.push({
        Header: 'SSO ID',
        accessor: 'ssoId',
        disableSortBy: true,
        headerStyle: { width: '130px', textAlign: 'center' },
        cellStyle: { width: '130px', textAlign: 'center' },
        Cell: OverflowCell,
      });
    }
    if (columnVisibility.smartabaseId && smartabaseEnabled) {
      cols.push({
        Header: 'Smartabase ID',
        accessor: 'smartabaseId',
        disableSortBy: true,
        headerStyle: { width: '150px', textAlign: 'center' },
        cellStyle: { width: '150px', textAlign: 'center' },
        Cell: OverflowCell,
      });
    }

    cols.push(
      {
        Header: 'Groups',
        id: 'groups',
        accessor: (row) => (row.groupAssignments || []).map((g) => g.name).join(' '),
        headerStyle: { width: '250px', textAlign: 'center' },
        cellStyle: { width: '250px', textAlign: 'center' },
        Cell: GroupsCell,
      },
      {
        Header: 'Calendar',
        accessor: 'calendarName',
        headerStyle: { width: '200px', textAlign: 'center' },
        cellStyle: { width: '200px', textAlign: 'center' },
        Cell: CalendarCell,
      },
    );

    if (columnVisibility.lastActivity) {
      cols.push({
        Header: 'Last Activity',
        accessor: 'lastActivity',
        disableSortBy: true,
        headerStyle: { width: '150px', textAlign: 'center' },
        cellStyle: { width: '150px', textAlign: 'center' },
        Cell: OverflowCell,
      });
    }
    if (columnVisibility.joinedDate) {
      cols.push({
        Header: 'Joined Date',
        accessor: 'joinedDate',
        disableSortBy: true,
        headerStyle: { width: '150px', textAlign: 'center' },
        cellStyle: { width: '150px', textAlign: 'center' },
        Cell: OverflowCell,
      });
    }

    cols.push(
      {
        Header: 'Status',
        accessor: 'activated',
        disableSortBy: true,
        headerStyle: { width: '130px', textAlign: 'center' },
        cellStyle: { width: '130px', textAlign: 'center' },
        Cell: StatusCell,
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        headerStyle: { width: '100px', textAlign: 'center' },
        cellStyle: { width: '100px', textAlign: 'center' },
        Cell: ActionDropdownCell,
      },
    );

    return cols;
  }, [allSelected, toggleAll, columnVisibility, usernamesEnabled, ssoEnabled, smartabaseEnabled]);

  const archivedTableData = useMemo(
    () => filteredAthletes.map((athlete) => ({
      ...athlete,
      onRestore: (athleteRow) => onRestoreAthlete(athleteRow),
      onDelete: (athleteRow) => onDeleteAthlete(athleteRow),
    })),
    [filteredAthletes, onDeleteAthlete, onRestoreAthlete],
  );

  const archivedColumns = useMemo(() => [
    {
      Header: 'User ID',
      accessor: 'userId',
      disableSortBy: true,
      headerStyle: { width: '200px', textAlign: 'center' },
      cellStyle: { width: '200px', textAlign: 'center' },
      Cell: OverflowCell,
    },
    {
      Header: 'First',
      accessor: 'first',
      sortType: nameSort,
      headerStyle: { width: '200px' },
    },
    {
      Header: 'Last',
      accessor: 'last',
      sortType: nameSort,
      headerStyle: { width: '200px' },
    },
    {
      Header: usernamesEnabled ? 'Username' : 'Email',
      accessor: usernamesEnabled ? 'username' : 'email',
      headerStyle: { minWidth: '300px', flex: '1' },
      cellStyle: { minWidth: '300px', flex: '1', overflow: 'hidden' },
    },
    {
      Header: 'Actions',
      id: 'archivedActions',
      disableSortBy: true,
      headerStyle: { width: '300px', textAlign: 'center' },
      cellStyle: { width: '300px', textAlign: 'center' },
      Cell: ArchivedActionsCell,
    },
  ], [usernamesEnabled]);

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: tableData,
      autoResetSortBy: false,
      initialState: { sortBy: [{ id: 'last', desc: false }] },
    },
    useSortBy,
  );

  const {
    getTableProps: getArchivedTableProps,
    headerGroups: archivedHeaderGroups,
    rows: archivedRows,
    prepareRow: prepareArchivedRow,
  } = useTable(
    {
      columns: archivedColumns,
      data: archivedTableData,
      autoResetSortBy: false,
      initialState: { sortBy: [{ id: 'last', desc: false }] },
    },
    useSortBy,
  );

  const renderTable = (tProps, hGroups, tRows, prep, isLoading) => {
    const tableMinWidth = hGroups[0]?.headers.reduce((sum, col) => {
      const hs = col.headerStyle || {};
      return sum + parseInt(hs.width || hs.minWidth || '0', 10);
    }, 0) || 800;

    return (
      <TableContainer>
        <HScroller>
          <HeaderRow style={{ minWidth: tableMinWidth }}>
            {hGroups[0].headers.map((column) => (
              <HeaderCell
                {...column.getHeaderProps(column.getSortByToggleProps({ title: undefined }))}
                canSort={column.canSort}
                style={{
                  flex: column.headerStyle?.flex,
                  width: column.headerStyle?.flex ? undefined : column.headerStyle?.width,
                  minWidth: column.headerStyle?.minWidth,
                  padding: column.headerStyle?.padding,
                  justifyContent: column.headerStyle?.textAlign === 'center' ? 'center' : undefined,
                }}
              >
                {column.render('Header')}
                {column.canSort && <SortIcon column={column} />}
              </HeaderCell>
            ))}
          </HeaderRow>
          {!isLoading && tRows.length > 0 && (
            <VirtualBody style={{ minWidth: tableMinWidth }}>
              <AutoSizer>
                {({ height, width }) => (
                  <FixedSizeList
                    height={height}
                    width={width}
                    itemCount={tRows.length}
                    itemSize={ROW_HEIGHT}
                    itemData={{ rows: tRows, prepareRow: prep }}
                  >
                    {RowRenderer}
                  </FixedSizeList>
                )}
              </AutoSizer>
            </VirtualBody>
          )}
          {isLoading && <SpinnerContainer><Spinner /></SpinnerContainer>}
          {!isLoading && tRows.length === 0 && (
            <EmptyStateDiv>
              {searchQuery.trim() ? 'No athletes match your search.' : 'No athletes found.'}
            </EmptyStateDiv>
          )}
        </HScroller>
      </TableContainer>
    );
  };

  return (
    <>
      <TabsRow>
        <TabButton
          type='button'
          active={!isArchived}
          onClick={() => onToggleArchived(false)}
        >
          Active Athletes
        </TabButton>
        <TabButton
          type='button'
          active={isArchived}
          onClick={() => onToggleArchived(true)}
        >
          Archived
        </TabButton>
      </TabsRow>

      <SearchRow>
        <SearchWrapper>
          <SearchIconWrapper>
            <Icon icon='magnifying-glass' size={16} color='#9E9E9E' />
          </SearchIconWrapper>
          <Input
            name='athlete-search'
            inputOnly
            placeholder={isArchived
              ? `Search archived athletes by name or ${usernamesEnabled ? 'username' : 'email'}`
              : `Search athletes by name, ${usernamesEnabled ? 'username' : 'email'}, or id`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            styledPropsInput={{
              width: '100%',
              height: '40px',
              fontSize: '14px',
              paddingLeft: '36px',
            }}
            stylePropsWrapper={{ border: '1px solid #EEE' }}
          />
        </SearchWrapper>

        {isArchived && archivedUserSlots != null && (
          <ArchiveBanner>
            <Text styledTextProps={{ fontSize: '13px', color: '#444444' }}>
              You are able to archive up to
            </Text>
            <Text styledTextProps={{ fontSize: '13px', fontWeight: 700, color: '#444444' }}>
              {archivedUserSlots}
            </Text>
            <Text styledTextProps={{ fontSize: '13px', color: '#444444' }}>
              athletes as part of your subscription. Please contact
            </Text>
            <Button
              name='Support'
              bgColor='transparent'
              noBorder
              txtColor='#77A7FF'
              styledWrapperProps={{ padding: '0' }}
              styledTextProps={{ fontSize: '13px', letterSpacing: 'normal', fontWeight: 600 }}
              onClick={onOpenSupport}
            />
            <Text styledTextProps={{ fontSize: '13px', color: '#444444' }}>
              to increase your limit.
            </Text>
          </ArchiveBanner>
        )}

        {!isArchived && (
          <FilterButtonWrapper>
            <Button
              name='Filter'
              icon='filter2'
              iconColor='#444444'
              bgColor='var(--theme-bg-surface, #FFF)'
              borderColor='var(--theme-border-default, #EEE)'
              customBorderRadius='4px'
              styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
              styledWrapperProps={{
                padding: '9px 18px',
                boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)',
              }}
              onClick={onOpenFilter}
            />
            {hasActiveFilters && <FilterBadge>{activeFiltersCount}</FilterBadge>}
          </FilterButtonWrapper>
        )}
      </SearchRow>

      {!isArchived && selectedIds.size > 0 && (
        <BulkActionsBar>
          <Button
            icon='clear-checkbox'
            iconSize={16}
            iconColor='#77A7FF'
            iconOnly
            noBorder
            onClick={() => setSelectedIds(new Set())}
            styledWrapperProps={{ padding: '0' }}
          />
          <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#77A7FF' }}>
            {`${selectedIds.size} ${selectedIds.size === 1 ? 'athlete' : 'athletes'} selected`}
          </Text>
          <BulkDropdownContainer ref={bulkDropdownRef}>
            <BulkDropdownButton
              type='button'
              onClick={() => setIsBulkDropdownOpen((prev) => !prev)}
            >
              Bulk Actions
              <Icon icon='gs-arrow-down' size={10} color='#fff' styledProps={{ marginLeft: '8px' }} />
            </BulkDropdownButton>
            {isBulkDropdownOpen && (
              <BulkDropdownMenu>
                <BulkDropdownItem type='button' onClick={() => { onBulkAction('changeCalendars', Array.from(selectedIds)); setIsBulkDropdownOpen(false); }}>
                  <Icon icon='calendar2' size={14} color='#9E9E9E' />
                  Change Calendars
                </BulkDropdownItem>
                <BulkDropdownItem type='button' onClick={() => { onBulkAction('changeGroups', Array.from(selectedIds)); setIsBulkDropdownOpen(false); }}>
                  <Icon icon='Users' size={14} color='#9E9E9E' />
                  Change Groups
                </BulkDropdownItem>
                <BulkDropdownItem type='button' onClick={() => { onBulkAction('assignPrograms', Array.from(selectedIds)); setIsBulkDropdownOpen(false); }}>
                  <Icon icon='templates' size={14} color='#9E9E9E' />
                  Assign Programs
                </BulkDropdownItem>
                <BulkDropdownItem type='button' onClick={() => { onBulkAction('emailAssessments', Array.from(selectedIds)); setIsBulkDropdownOpen(false); }}>
                  <Icon icon='share-ams' size={14} color='#9E9E9E' />
                  Email Assessments
                </BulkDropdownItem>
                {accountsList?.length > 1 && (
                  <>
                    <BulkDropdownDivider />
                    <BulkDropdownItem type='button' onClick={() => { onBulkAction('moveAccount', Array.from(selectedIds)); setIsBulkDropdownOpen(false); }}>
                      <Icon icon='swap-alt' size={14} color='#9E9E9E' />
                      Move to New Account
                    </BulkDropdownItem>
                  </>
                )}
                <BulkDropdownDivider />
                <BulkDropdownItem destructive type='button' onClick={() => { onBulkAction('deleteAthletes', Array.from(selectedIds)); setIsBulkDropdownOpen(false); }}>
                  <Icon icon='delete' size={14} color='#FF0505' />
                  Delete
                </BulkDropdownItem>
              </BulkDropdownMenu>
            )}
          </BulkDropdownContainer>
        </BulkActionsBar>
      )}

      {searchQuery.trim() && (
        <ResultsText>
          {isArchived
            ? `Showing ${filteredAthletes.length} of ${archivedAthletes.length} athletes`
            : `Showing ${filteredAthletes.length} of ${athletes.length} athletes`}
        </ResultsText>
      )}

      {isArchived
        ? renderTable(
          getArchivedTableProps,
          archivedHeaderGroups,
          archivedRows,
          prepareArchivedRow,
          isLoadingArchivedAthletes,
        )
        : renderTable(
          getTableProps,
          headerGroups,
          rows,
          prepareRow,
          isLoadingAthletes,
        )}
    </>
  );
};

ManageAthletesTable.propTypes = {
  onEditAthlete: PropTypes.func.isRequired,
  onDeleteAthlete: PropTypes.func.isRequired,
  onResendActivation: PropTypes.func.isRequired,
  onArchiveAthlete: PropTypes.func.isRequired,
  onRestoreAthlete: PropTypes.func.isRequired,
  onToggleArchived: PropTypes.func.isRequired,
  onOpenSupport: PropTypes.func.isRequired,
  onBulkAction: PropTypes.func.isRequired,
  isArchived: PropTypes.bool,
  archivedUserSlots: PropTypes.number,
  columnVisibility: PropTypes.shape({
    userId: PropTypes.bool,
    externalId: PropTypes.bool,
    ssoId: PropTypes.bool,
    smartabaseId: PropTypes.bool,
    lastActivity: PropTypes.bool,
    joinedDate: PropTypes.bool,
  }),
  onOpenFilter: PropTypes.func,
  hasActiveFilters: PropTypes.bool,
  activeFiltersCount: PropTypes.number,
  onVisibleAthletesChange: PropTypes.func,
};

ManageAthletesTable.defaultProps = {
  isArchived: false,
  archivedUserSlots: null,
  columnVisibility: {
    userId: false,
    externalId: false,
    ssoId: false,
    smartabaseId: false,
    lastActivity: false,
    joinedDate: false,
  },
  onOpenFilter: () => {},
  hasActiveFilters: false,
  activeFiltersCount: 0,
  onVisibleAthletesChange: () => {},
};

export default ManageAthletesTable;
